/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { type DocNode } from '@atlaskit/adf-schema';
import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { editorCardProvider } from '@atlaskit/editor-card-provider';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { createDefaultPreset } from '@atlaskit/editor-core/preset-default';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { cardPlugin } from '@atlaskit/editor-plugin-card';
import { contentInsertionPlugin } from '@atlaskit/editor-plugin-content-insertion';
import { gridPlugin } from '@atlaskit/editor-plugin-grid';
import { listPlugin } from '@atlaskit/editor-plugin-list';
import { mentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import { rulePlugin } from '@atlaskit/editor-plugin-rule';
import { tablesPlugin } from '@atlaskit/editor-plugin-table';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Heading from '@atlaskit/heading';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';
import PageIcon from '@atlaskit/icon/core/page';
import InlineEdit, { InlineEditableTextfield } from '@atlaskit/inline-edit';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';
import type { ConfluenceSpaceOption } from '@atlassian/rovo-platform-ui-components/base-space-selector/types';
import { ConfluenceSpaceSelector } from '@atlassian/rovo-platform-ui-components/confluence-space-selector';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

const styles = cssMap({
	pageIcon: {
		paddingTop: token('space.025'),
	},

	pageTitleWrapper: {
		flexGrow: 1,
		marginTop: token('space.negative.100'),
	},

	contentWrapper: {
		marginTop: token('space.negative.200'),
		marginBottom: token('space.negative.100'),
	},

	pageTitleReadView: {
		display: 'flex',
		font: token('font.body'),
		maxWidth: '100%',
		wordBreak: 'break-word',
		paddingBlock: token('space.050'),
		paddingInline: token('space.075'),
	},
});

const ConfluencePageCreateActionNewLayout = ({
	onCustomDataConfirm,
	actions,
	getStatus,
	getReadonly,
	cloudId,
	aaid,
	productKey,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.ConfluencePageCreate>): JSX.Element => {
	di(ComposableEditor);
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const firstAction = actions[0];
	const { data } = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const [name, setName] = useState(data.name);
	const [selectedSpace, setSelectedSpace] = useState<ConfluenceSpaceOption | undefined>(undefined);
	const [content, setContent] = useState<DocNode | undefined>(
		data.content?.length ? JSON.parse(data.content) : undefined,
	);
	const { getEntity } = useConfluenceEntities(cloudId);

	const isActionLoading = status !== 'idle';
	const isPublishDisabled = isActionLoading || readonly || !name || !selectedSpace || !content;
	const isEditorDisabled = isActionLoading || readonly || !name;
	const isSubmitting = status === 'started';

	const linkPicker = useLinkPickerEditorProps(undefined, {
		cloudId,
		product: productKey,
	});
	const { preset } = usePreset(() =>
		createDefaultPreset({
			placeholder: {
				placeholder: formatMessage(messages.pageContentPlaceholder),
			},
		})
			.add(contentInsertionPlugin)
			.add([tablesPlugin, { isChromelessEditor: true, tableOptions: {} }])
			.add(mentionsPlugin)
			.add(gridPlugin)
			.add([
				cardPlugin,
				{
					allowBlockCards: false,
					allowEmbeds: false,
					linkPicker,
					provider: Promise.resolve(editorCardProvider),
				},
			])
			.add(listPlugin)
			.add(rulePlugin),
	);

	useEffect(() => {
		const fetchSpace = async () => {
			if (data.folder) {
				const space = await getEntity(data.folder, 'space');

				if (space) {
					setSelectedSpace({
						label: space.name,
						value: space.id,
						iconPath: space.icon?.path,
						key: space.key,
					});
				}
			}
		};

		fetchSpace();
	}, [data.folder, getEntity]);

	const handleSubmit = useCallback(() => {
		const mergedData = {
			...data,
			extraInputs: {
				...data.extraInputs,
				representation: 'atlas_doc_format',
			},
			name,
			folder: selectedSpace?.value as string,
			content: JSON.stringify(content),
		};

		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const plannedActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'confluencePageCreateRenderer',
				},
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: plannedActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', mergedData)
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onCustomDataConfirm(firstAction, mergedData);
	}, [
		onCustomDataConfirm,
		data,
		name,
		selectedSpace?.value,
		content,
		firstAction,
		actions,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		getStatus,
	]);

	// determine if we should render the live doc elements
	const shouldRenderLiveDocUI =
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(data.extraInputs as any)?.subtype === 'live' &&
		fg('rovo_create_page_action_enable_live_doc_creation');

	return (
		<Stack space="space.250">
			<Text>
				{shouldRenderLiveDocUI
					? formatMessage(messages.actionMessageLiveDoc, {
							spaceName: selectedSpace?.label ?? '',
							bold: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
						})
					: formatMessage(messages.actionMessage, {
							spaceName: selectedSpace?.label ?? '',
							bold: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
						})}
			</Text>
			<ActionCardWrapper
				title={formatMessage(messages.confluencePageCreateActionCardTitle)}
				product="confluence"
			>
				<Stack space="space.200">
					<Inline space="space.100" alignBlock="center">
						<Heading size="xsmall">{formatMessage(messages.spaceSelectorLabel)}</Heading>
						{/* The user should always see the full space selector unless the action is still loading. */}
						{isActionLoading ? (
							<Skeleton height={30} width={200} borderRadius={token('radius.large')} />
						) : (
							<ConfluenceSpaceSelector
								cloudId={cloudId}
								userId={aaid}
								testId="confluence-page-create-space-selector"
								// If the editor is disabled then the selector should be disabled rather than hidden.
								isDisabled={isEditorDisabled}
								onMenuOpen={() => {
									sendAnalyticsEvent({
										action: 'opened',
										actionSubject: 'dropdown',
										actionSubjectId: 'rovoConfluencePageCreateSpaceSelector',
									});
								}}
								isRequired
								onChange={(option: ConfluenceSpaceOption | null) => {
									if (option) {
										setSelectedSpace(option);
										sendAnalyticsEvent({
											action: 'selected',
											actionSubject: 'dropdown',
											actionSubjectId: 'rovoConfluencePageCreateSpaceSelector',
										});
									}
								}}
								value={selectedSpace}
							/>
						)}
					</Inline>

					<Inline space="space.025" alignBlock="center" xcss={styles.pageTitleWrapper}>
						<Box xcss={styles.pageIcon}>
							{shouldRenderLiveDocUI ? (
								<PageLiveDocIcon spacing="none" label="" color="var(--ds-icon-brand)" />
							) : (
								<PageIcon spacing="none" label="" color="var(--ds-icon-brand)" />
							)}
						</Box>
						{isEditorDisabled ? (
							<Box xcss={styles.pageTitleReadView}>
								{name || formatMessage(messages.pageContentPlaceholder)}
							</Box>
						) : (
							<Box xcss={styles.pageTitleWrapper}>
								<InlineEditableTextfield
									testId="page-name-input-field"
									isRequired
									isCompact
									readViewFitContainerWidth
									defaultValue={name}
									onConfirm={(value) => {
										setName(value);
										sendAnalyticsEvent({
											action: 'edited',
											actionSubject: 'inlineEdit',
											actionSubjectId: 'rovoConfluencePageCreateTitle',
										});
									}}
									onCancel={() => {
										sendAnalyticsEvent({
											action: 'cancelled',
											actionSubject: 'inlineEdit',
											actionSubjectId: 'rovoConfluencePageCreateTitle',
										});
									}}
									placeholder={formatMessage(messages.pageContentPlaceholder)}
								/>
							</Box>
						)}
					</Inline>

					<Box xcss={styles.contentWrapper}>
						{isEditorDisabled ? (
							<ComposableEditor
								disabled={isEditorDisabled}
								appearance="chromeless"
								preset={preset}
								defaultValue={content}
							/>
						) : (
							<InlineEdit<DocNode>
								isRequired
								defaultValue={content}
								readViewFitContainerWidth
								readView={() => (
									<ComposableEditor
										appearance="chromeless"
										preset={preset}
										defaultValue={content}
									/>
								)}
								editView={(props) => (
									<ComposableEditor
										{...props}
										shouldFocus
										appearance="chromeless"
										preset={preset}
										onChange={(e) => {
											props.onChange(e.state.doc.toJSON());
										}}
										defaultValue={content}
									/>
								)}
								onEdit={() => {
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluencePageCreateContent',
									});
								}}
								onConfirm={(value) => {
									setContent(value);
									sendAnalyticsEvent({
										action: 'edited',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluencePageCreateContent',
									});
								}}
								onCancel={() => {
									sendAnalyticsEvent({
										action: 'cancelled',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluencePageCreateContent',
									});
								}}
							/>
						)}
					</Box>
				</Stack>

				<ActionCardFooter>
					<Button
						appearance="primary"
						isLoading={isSubmitting}
						isDisabled={isPublishDisabled}
						onClick={(_, e) => {
							handleSubmit();

							sendAnalyticsEvent(
								e.update({
									actionSubjectId: 'rovoConfluencePageCreateSubmit',
								}),
							);
						}}
					>
						{shouldRenderLiveDocUI
							? formatMessage(messages.createLiveDocButton)
							: formatMessage(messages.publishPageButton)}
					</Button>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};

export const ConfluencePageCreateAction = (
	props: ActionRendererFor<typeof ActionKeys.ConfluencePageCreate>,
): JSX.Element => {
	return <ConfluencePageCreateActionNewLayout {...props} />;
};
